import React from 'react';
import PropTypes from 'prop-types';
import { Group, Line, Circle, Text } from 'react-konva';
import RangeCalculator from '../../../utils/RangeCalculator';

/**
 * MeasureOverlay - D&D 5e measurement overlay with proper distance calculations
 * date_of_change: 2025-08-30 - Updated to use RangeCalculator for accurate D&D 5e distances
 *
 * Props:
 * - active: boolean - whether to render the overlay
 * - startCell: { x, y } | null - starting grid cell (integer indices)
 * - endCell: { x, y } | null - ending grid cell (integer indices)
 * - gridSize: number - pixels per grid cell
 * - feetPerCell: number - distance represented by each cell (default 5ft)
 * - gridType: string - 'square' or 'hex' grid type
 * - color: string - stroke and text color
 * - strokeWidth: number - line stroke width in pixels
 */
const MeasureOverlay = ({
  active = false,
  startCell = null,
  endCell = null,
  gridSize = 50,
  feetPerCell = 5,
  gridType = 'square',
  color = '#00e5ff',
  strokeWidth = 3,
}) => {
  if (!active || !startCell || !endCell || !gridSize) return null;

  // Convert a grid cell (integer index) to pixel center
  const cellCenterToPx = (cell) => ({
    x: (cell.x + 0.5) * gridSize,
    y: (cell.y + 0.5) * gridSize,
  });

  const a = cellCenterToPx(startCell);
  const b = cellCenterToPx(endCell);

  // Use RangeCalculator for proper D&D 5e distance calculations
  const distance = RangeCalculator.calculateGridDistance(startCell, endCell, gridType, feetPerCell);
  
  // Create detailed label with D&D 5e information
  let label = `${distance.feet} ft (${distance.cells} ${distance.cells === 1 ? 'cell' : 'cells'})`;
  
  // Add diagonal movement info for square grids
  if (gridType === 'square' && distance.isDiagonal) {
    label += `\nDiagonal: ${distance.horizontal}x${distance.vertical}`;
    if (distance.horizontal > 0 && distance.vertical > 0) {
      // Show the actual D&D 5e diagonal cost
      const straight = Math.min(distance.horizontal, distance.vertical);
      const remaining = Math.max(distance.horizontal, distance.vertical) - straight;
      let diagonalCost = 0;
      
      // Calculate true diagonal cost (5ft, 10ft, 5ft, 10ft pattern)
      for (let i = 0; i < straight; i++) {
        diagonalCost += (i % 2 === 0) ? 5 : 10;
      }
      diagonalCost += remaining * 5;
      
      if (diagonalCost !== distance.feet) {
        label += `\nTrue cost: ${diagonalCost} ft`;
      }
    }
  }

  // Offset label a bit from the end point
  const labelOffset = { x: 10, y: -10 };

  return (
    <Group listening={false}>
      {/* endpoints */}
      <Circle x={a.x} y={a.y} radius={5} fill={color} opacity={0.9} />
      <Circle x={b.x} y={b.y} radius={5} fill={color} opacity={0.9} />

      {/* line */}
      <Line
        points={[a.x, a.y, b.x, b.y]}
        stroke={color}
        strokeWidth={strokeWidth}
        dash={[10, 8]}
        lineCap="round"
        lineJoin="round"
        opacity={0.9}
      />

      {/* label */}
      <Text
        x={b.x + labelOffset.x}
        y={b.y + labelOffset.y}
        text={label}
        fill={color}
        fontSize={14}
        fontStyle="bold"
        shadowColor="#000"
        shadowBlur={4}
        shadowOpacity={0.6}
      />
    </Group>
  );
};

MeasureOverlay.propTypes = {
  active: PropTypes.bool,
  startCell: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
  endCell: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
  gridSize: PropTypes.number,
  feetPerCell: PropTypes.number,
  gridType: PropTypes.oneOf(['square', 'hex']),
  color: PropTypes.string,
  strokeWidth: PropTypes.number,
};

export default MeasureOverlay;
